import { FILE_SIZE_LIMITS, UserTier } from '../types';

/**
 * Format bytes to human-readable string
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

/**
 * Validate file size based on user tier
 * Returns error message if file is too large, null otherwise
 */
export function validateFileSize(
  fileSize: number,
  userTier: UserTier
): string | null {
  const limit = FILE_SIZE_LIMITS[userTier];

  if (limit === null) {
    // Unlimited
    return null;
  }

  if (fileSize > limit) {
    return `File size ${formatBytes(fileSize)} exceeds your tier limit of ${formatBytes(limit)}`;
  }

  return null;
}

/**
 * Get file size limit description for user tier
 */
export function getFileSizeLimitDescription(userTier: UserTier): string {
  const limit = FILE_SIZE_LIMITS[userTier];
  if (limit === null) {
    return 'Unlimited';
  }
  return formatBytes(limit);
}

/**
 * Download blob as file
 */
export function downloadFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Generate a random channel name for WebRTC signaling
 */
export function generateChannelName(): string {
  return `file-share-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Format timestamp to readable date string
 */
export function formatDate(timestamp: string | Date): string {
  const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
}

/**
 * Calculate transfer speed in bytes per second
 */
export function calculateTransferSpeed(
  bytesTransferred: number,
  startTime: number
): string {
  const elapsedSeconds = (Date.now() - startTime) / 1000;
  if (elapsedSeconds === 0) return '0 B/s';
  
  const speed = bytesTransferred / elapsedSeconds;
  return `${formatBytes(speed)}/s`;
}
